import { bindTextures, createFramebuffer, createProgram, createTexture, emptyVAO, uniforms, type GLContext } from '../gl/context';
import { FULLSCREEN_VS, GRID } from '../shaders/common';
import { SCATTER_FS1, SCATTER_FS2, SCATTER_VS, UPDATE_FS } from '../shaders/sim';

export interface StepUniforms {
  time: number;
  anchor: [number, number, number];
  boxMin: [number, number, number];
  boxSize: [number, number, number];
  /** cohesion, separation, alignment, cruise speed */
  b1: [number, number, number, number];
  /** nervousness, turn rate, flap rate, crowding */
  b2: [number, number, number, number];
  spread: number;
  /** Falcon position and force radius (0 = none). */
  predator: [number, number, number, number];
  predatorVel: [number, number, number];
  /** Seeded point startle: position and radius (0 = none). */
  startle: [number, number, number, number];
  /** Excitation per second beside a fully agitated neighbourhood. */
  waveRate: number;
  alarm: number;
  alarmFrom: [number, number, number];
}

const SIM_WIDTH = 512;

interface StateSet {
  pos: WebGLTexture;
  vel: WebGLTexture;
  aux: WebGLTexture;
  fbo: WebGLFramebuffer;
}

/**
 * The whole flock, on the GPU. Three state textures (position, velocity,
 * auxiliary) in a ping-pong pair, two field atlases (fine: count and summed
 * velocity plus a second target for summed agitation; coarse: count and
 * summed relative position), one-texel whole-flock sums, and three programs:
 * scatter with two outputs, scatter with one, update.
 */
export class FlockSim {
  readonly texW: number;
  readonly texH: number;
  readonly capacity: number;
  /** Birds simulated and drawn this frame. Never more than capacity. */
  active: number;
  /** True when the field atlases are 32-bit floats (exact counts); false on the half-float fallback. */
  readonly fields32: boolean;

  private readonly gl: WebGL2RenderingContext;
  private states: [StateSet, StateSet];
  private cur = 0;
  private readonly fine: { count: WebGLTexture; agit: WebGLTexture; fbo: WebGLFramebuffer };
  private readonly coarse: { tex: WebGLTexture; fbo: WebGLFramebuffer };
  private readonly global: { tex: WebGLTexture; pos: WebGLTexture; fbo: WebGLFramebuffer };
  private readonly readback = new Float32Array(8);
  private readonly pbo: WebGLBuffer;
  private sync: WebGLSync | null = null;
  private pending: { anchor: [number, number, number]; boxSize: [number, number, number] } | null = null;
  private canReadFloat: boolean | null = null;
  /** True when the one-texel whole-flock sums are 32-bit floats, which the centre readback needs. */
  private readonly globals32: boolean;
  private readonly scatter2: WebGLProgram;
  private readonly scatter1: WebGLProgram;
  private readonly update: WebGLProgram;
  private readonly uS2;
  private readonly uS1;
  private readonly uU;
  private readonly vao: WebGLVertexArrayObject;
  private seed: number;

  constructor(ctx: GLContext, count: number, seed: number) {
    const gl = ctx.gl;
    this.gl = gl;
    this.texW = Math.min(SIM_WIDTH, ctx.maxTextureSize);
    this.texH = Math.max(1, Math.ceil(count / this.texW));
    this.capacity = this.texW * this.texH;
    this.active = Math.min(count, this.capacity);
    this.seed = seed >>> 0;

    const mk = (): StateSet => {
      const spec = (internal: number, type: number) => ({
        width: this.texW,
        height: this.texH,
        internalFormat: internal,
        format: gl.RGBA,
        type,
        filter: gl.NEAREST,
      });
      const pos = createTexture(gl, spec(gl.RGBA32F, gl.FLOAT));
      const vel = createTexture(gl, spec(gl.RGBA32F, gl.FLOAT));
      const aux = createTexture(gl, spec(gl.RGBA16F, gl.HALF_FLOAT));
      return { pos, vel, aux, fbo: createFramebuffer(gl, [pos, vel, aux], 'state') };
    };
    this.states = [mk(), mk()];

    // Field sums are additive blends of one increment per bird. Half floats
    // stop growing after ~2048 equal increments (11-bit mantissa), which a
    // dense cell or the whole-flock texel exceeds easily, so use 32-bit
    // floats wherever the GPU can blend into them, and filter them linearly
    // only where it can.
    const atlas32 = ctx.floatBlend && ctx.floatLinear;
    this.fields32 = atlas32;
    this.globals32 = ctx.floatBlend;
    const fieldSpec = (w: number, h: number, wide = atlas32) => ({
      width: w,
      height: h,
      internalFormat: wide ? gl.RGBA32F : gl.RGBA16F,
      format: gl.RGBA,
      type: wide ? gl.FLOAT : gl.HALF_FLOAT,
      filter: gl.LINEAR,
    });
    const fc = createTexture(gl, fieldSpec(GRID.fine.atlas[0], GRID.fine.atlas[1]));
    const fa = createTexture(gl, fieldSpec(GRID.fine.atlas[0], GRID.fine.atlas[1]));
    this.fine = { count: fc, agit: fa, fbo: createFramebuffer(gl, [fc, fa], 'fine') };
    const ct = createTexture(gl, fieldSpec(GRID.coarse.atlas[0], GRID.coarse.atlas[1]));
    this.coarse = { tex: ct, fbo: createFramebuffer(gl, [ct], 'coarse') };
    // The one-texel sums only need blending, never filtering.
    const gt = createTexture(gl, { ...fieldSpec(1, 1, ctx.floatBlend), filter: gl.NEAREST });
    const gp = createTexture(gl, { ...fieldSpec(1, 1, ctx.floatBlend), filter: gl.NEAREST });
    this.global = { tex: gt, pos: gp, fbo: createFramebuffer(gl, [gt, gp], 'global') };
    // Pixel pack buffer for the asynchronous centre readback: two RGBA32F texels.
    this.pbo = gl.createBuffer();
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.pbo);
    gl.bufferData(gl.PIXEL_PACK_BUFFER, 32, gl.DYNAMIC_READ);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);

    this.scatter2 = createProgram(gl, SCATTER_VS, SCATTER_FS2, 'scatter2');
    this.scatter1 = createProgram(gl, SCATTER_VS, SCATTER_FS1, 'scatter1');
    this.update = createProgram(gl, FULLSCREEN_VS, UPDATE_FS, 'update');
    const scatterNames = ['uPos', 'uVel', 'uSimSize', 'uBoxMin', 'uBoxSize', 'uAnchor', 'uG', 'uTX', 'uAtlas', 'uCoarse', 'uScale'] as const;
    this.uS2 = uniforms(gl, this.scatter2, scatterNames);
    this.uS1 = uniforms(gl, this.scatter1, scatterNames);
    this.uU = uniforms(gl, this.update, [
      'uPos', 'uVel', 'uAux', 'uFine', 'uAgit', 'uCoarse', 'uGlobal', 'uSimSize', 'uMode', 'uDt', 'uTime', 'uSeed',
      'uAnchor', 'uBoxMin', 'uBoxSize', 'uB1', 'uB2', 'uSpread',
      'uPredator', 'uPredatorVel', 'uStartle', 'uWaveRate', 'uAlarm', 'uAlarmFrom',
    ] as const);
    this.vao = emptyVAO(gl);
  }

  get pos(): WebGLTexture { return this.states[this.cur]!.pos; }
  get vel(): WebGLTexture { return this.states[this.cur]!.vel; }
  get aux(): WebGLTexture { return this.states[this.cur]!.aux; }

  /** Rows of the state texture that hold active birds. */
  private get activeRows(): number {
    return Math.max(1, Math.ceil(this.active / this.texW));
  }

  reinit(seed: number, u: StepUniforms): void {
    this.seed = seed >>> 0;
    this.runUpdate(0, 0, u, this.texH);
  }

  step(dt: number, u: StepUniforms): void {
    const gl = this.gl;
    const rows = this.activeRows;
    gl.bindVertexArray(this.vao);
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE, gl.ONE);

    const st = this.states[this.cur]!;

    // Fine field: count + summed velocity, and summed agitation (magnitude and signed).
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fine.fbo);
    gl.viewport(0, 0, GRID.fine.atlas[0], GRID.fine.atlas[1]);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.scatter2);
    bindTextures(gl, [st.pos, st.vel]);
    this.setScatterUniforms(this.uS2, u, GRID.fine, 0);
    gl.drawArrays(gl.POINTS, 0, this.active);

    // Coarse field: count + summed relative position.
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.coarse.fbo);
    gl.viewport(0, 0, GRID.coarse.atlas[0], GRID.coarse.atlas[1]);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.scatter1);
    this.setScatterUniforms(this.uS1, u, GRID.coarse, 1);
    gl.drawArrays(gl.POINTS, 0, this.active);

    // Whole-flock mean velocity and mean position in one texel each.
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.global.fbo);
    gl.viewport(0, 0, 1, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(this.scatter2);
    this.setScatterUniforms(this.uS2, u, GRID.global, 2);
    gl.drawArrays(gl.POINTS, 0, this.active);

    gl.disable(gl.BLEND);
    this.runUpdate(1, dt, u, rows);
  }

  /**
   * Whole-flock centre of mass in world space, or null when it is not (yet)
   * known. Asynchronous: a call issues a readback into a pixel pack buffer
   * and fences it; a later call collects it once the GPU has signalled, so
   * the main thread never waits on the pipeline. The result is a step or
   * two stale, which the falcon does not mind. Null for good when the sums
   * are half floats (they saturate) or the GPU will not hand floats back.
   */
  readCenter(u: StepUniforms): [number, number, number] | null {
    if (!this.globals32 || this.canReadFloat === false) return null;
    const gl = this.gl;

    if (this.sync) {
      if (gl.getSyncParameter(this.sync, gl.SYNC_STATUS) !== gl.SIGNALED) return null;
      gl.deleteSync(this.sync);
      this.sync = null;
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.pbo);
      gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, this.readback);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      const p = this.pending!;
      this.pending = null;
      const count = this.readback[0]!;
      if (!(count > 1e-6)) return null;
      return [
        p.anchor[0] + (this.readback[4]! / count) * p.boxSize[0] * 0.5,
        p.anchor[1] + (this.readback[5]! / count) * p.boxSize[1] * 0.5,
        p.anchor[2] + (this.readback[6]! / count) * p.boxSize[2] * 0.5,
      ];
    }

    // Issue a new request. Drain any earlier error first so the one-time probe
    // below only sees this readback's own verdict.
    if (this.canReadFloat === null) gl.getError();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.global.fbo);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, this.pbo);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.FLOAT, 0);
    gl.readBuffer(gl.COLOR_ATTACHMENT1);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.FLOAT, 16);
    gl.readBuffer(gl.COLOR_ATTACHMENT0);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (this.canReadFloat === null) {
      // The spec allows RGBA/FLOAT reads from float buffers, but be sure.
      this.canReadFloat = gl.getError() === gl.NO_ERROR;
      if (!this.canReadFloat) return null;
    }
    this.sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    this.pending = { anchor: [u.anchor[0], u.anchor[1], u.anchor[2]], boxSize: [u.boxSize[0], u.boxSize[1], u.boxSize[2]] };
    return null;
  }

  private setScatterUniforms(
    U: typeof this.uS2,
    u: StepUniforms,
    grid: (typeof GRID)['fine'] | (typeof GRID)['coarse'] | (typeof GRID)['global'],
    mode: number,
  ): void {
    const gl = this.gl;
    gl.uniform1i(U.uPos, 0);
    gl.uniform1i(U.uVel, 1);
    gl.uniform2i(U.uSimSize, this.texW, this.texH);
    gl.uniform3fv(U.uBoxMin, u.boxMin);
    gl.uniform3fv(U.uBoxSize, u.boxSize);
    gl.uniform3fv(U.uAnchor, u.anchor);
    gl.uniform3f(U.uG, grid.g[0], grid.g[1], grid.g[2]);
    gl.uniform1f(U.uTX, grid.tilesX);
    gl.uniform2f(U.uAtlas, grid.atlas[0], grid.atlas[1]);
    gl.uniform1i(U.uCoarse, mode);
    gl.uniform1f(U.uScale, 1 / grid.scale);
  }

  private runUpdate(mode: number, dt: number, u: StepUniforms, rows: number): void {
    const gl = this.gl;
    const src = this.states[this.cur]!;
    const dst = this.states[1 - this.cur]!;
    gl.bindVertexArray(this.vao);
    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fbo);
    gl.viewport(0, 0, this.texW, rows);
    gl.useProgram(this.update);
    bindTextures(gl, [src.pos, src.vel, src.aux, this.fine.count, this.fine.agit, this.coarse.tex, this.global.tex]);
    const U = this.uU;
    gl.uniform1i(U.uPos, 0);
    gl.uniform1i(U.uVel, 1);
    gl.uniform1i(U.uAux, 2);
    gl.uniform1i(U.uFine, 3);
    gl.uniform1i(U.uAgit, 4);
    gl.uniform1i(U.uCoarse, 5);
    gl.uniform1i(U.uGlobal, 6);
    gl.uniform2i(U.uSimSize, this.texW, this.texH);
    gl.uniform1i(U.uMode, mode);
    gl.uniform1f(U.uDt, dt);
    gl.uniform1f(U.uTime, u.time);
    gl.uniform1ui(U.uSeed, this.seed);
    gl.uniform3fv(U.uAnchor, u.anchor);
    gl.uniform3fv(U.uBoxMin, u.boxMin);
    gl.uniform3fv(U.uBoxSize, u.boxSize);
    gl.uniform4fv(U.uB1, u.b1);
    gl.uniform4fv(U.uB2, u.b2);
    gl.uniform1f(U.uSpread, u.spread);
    gl.uniform4fv(U.uPredator, u.predator);
    gl.uniform3fv(U.uPredatorVel, u.predatorVel);
    gl.uniform4fv(U.uStartle, u.startle);
    gl.uniform1f(U.uWaveRate, u.waveRate);
    gl.uniform1f(U.uAlarm, u.alarm);
    gl.uniform3fv(U.uAlarmFrom, u.alarmFrom);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.cur = 1 - this.cur;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  dispose(): void {
    const gl = this.gl;
    for (const s of this.states) {
      gl.deleteTexture(s.pos);
      gl.deleteTexture(s.vel);
      gl.deleteTexture(s.aux);
      gl.deleteFramebuffer(s.fbo);
    }
    gl.deleteTexture(this.fine.count);
    gl.deleteTexture(this.fine.agit);
    gl.deleteFramebuffer(this.fine.fbo);
    gl.deleteTexture(this.coarse.tex);
    gl.deleteFramebuffer(this.coarse.fbo);
    gl.deleteTexture(this.global.tex);
    gl.deleteTexture(this.global.pos);
    gl.deleteFramebuffer(this.global.fbo);
    if (this.sync) gl.deleteSync(this.sync);
    gl.deleteBuffer(this.pbo);
    gl.deleteProgram(this.scatter2);
    gl.deleteProgram(this.scatter1);
    gl.deleteProgram(this.update);
    gl.deleteVertexArray(this.vao);
  }
}
